import ReactHtmlParser from 'react-html-parser';
import styles from './LessonContent.module.css';
import { Button } from '../../../components';
import { Pane, PaperclipIcon, Text } from 'evergreen-ui';
import { ImageGallery } from '../../ImageGallery';
import QuizContent from './QuizContent';
import { SCORMContents } from './SCORMContents';
import { useCallback } from 'react';

const LessonContent = ({
    content,
    media,
    setSelectedLessonRef,
    record,
    recordType,
    lesson,
    course,
    refreshData,
    onScormLessonStatusChanged,
}) => {
    const onLessonStatusChanged = useCallback(
        ({ status }) => {
            onScormLessonStatusChanged({
                blockId: content._id,
                lessonStatus: status,
            });
        },
        [content, onScormLessonStatusChanged]
    );

    const getMediaUrl = (media_ref) => {
        const foundMediaItem = media.find((m) => m._id === media_ref);
        return foundMediaItem?.url || '';
    };

    switch (content.type) {
        case 'text':
            return ReactHtmlParser(content.value);

        case 'audio':
            const audioUrl = getMediaUrl(content.media_ref);

            if (!audioUrl) return null;
            return (
                <div style={{ margin: '20px 0' }}>
                    <audio controls controlsList="nodownload" className={styles.audio_player}>
                        <source src={audioUrl} />
                    </audio>
                </div>
            );

        case 'video':
            const video = content.media_ref ? media.find((m) => m._id === content.media_ref) : null;

            let videoElement = null;
            if (video && video.view_config && video.view_config.type === 'bunny') {
                videoElement = (
                    <div
                        style={{
                            position: 'relative',
                            paddingTop: '62.5%',
                            margin: '30px 0',
                        }}
                    >
                        <iframe
                            title="Video"
                            src={`https://iframe.mediadelivery.net/embed/${video.view_config.library_id}/${video.view_config.video_id}?autoplay=false`}
                            loading="lazy"
                            style={{
                                border: 'none',
                                position: 'absolute',
                                top: 0,
                                height: '100%',
                                width: '100%',
                            }}
                            allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                            allowfullscreen="true"
                        ></iframe>
                    </div>
                );
            }

            return videoElement;

        case 'image':
            const imageUrls = content.media_refs.map(getMediaUrl);
            return (
                <div style={{ margin: '20px 0' }}>
                    <ImageGallery
                        img_urls={imageUrls}
                        onClick={(img_url) => window.open(img_url)}
                    />
                </div>
            );

        case 'button':
            const handleClick = () => {
                const { action, url, lesson_ref } = content.config;
                if (action === 'external_link' && url) {
                    window.open(url);
                } else if (action === 'lesson_link' && lesson_ref) {
                    setSelectedLessonRef(lesson_ref);
                }
            };

            return (
                <div style={{ display: 'flex', justifyContent: 'center', margin: '20px 0' }}>
                    <Button onClick={handleClick} appearance="primary">
                        {content.config.label}
                    </Button>
                </div>
            );

        case 'attachment':
            return (
                <Pane display="flex" flexDirection="column" gap={5}>
                    {(content.media_refs || []).map((media_ref) => {
                        const media_item = media.find((m) => m._id === media_ref);
                        if (!media_item) {
                            return null;
                        }
                        return (
                            <Pane
                                key={media_ref}
                                className={`${styles.attachment} ${styles.attachment_preview}`}
                                onClick={() => {
                                    if (media_item?.url) window.open(media_item.url);
                                }}
                            >
                                <Pane className={styles.attachment_name}>
                                    <PaperclipIcon color="gray" />
                                    <Text size={500} fontWeight={600} marginLeft={5}>
                                        {media_item.name}
                                    </Text>
                                </Pane>
                            </Pane>
                        );
                    })}
                </Pane>
            );

        case 'embed':
            return ReactHtmlParser(content.value);

        case 'quiz':
            return (
                <QuizContent
                    content={content}
                    record={record}
                    recordType={recordType}
                    course={course}
                    lesson={lesson}
                    refreshData={refreshData}
                />
            );

        case 'scorm': {
            const scormMedia = media.find((media) => media._id === content.media_ref);

            if (!scormMedia || !scormMedia.entry_url) {
                console.error('SCORM media not found');
                return null;
            }

            const entryUrl = `${scormMedia._id}/${scormMedia.entry_url}`;

            return (
                <SCORMContents
                    entityId={record._id}
                    lessonId={lesson._id}
                    blockId={content._id}
                    entryUrl={entryUrl}
                    onLessonStatusChanged={onLessonStatusChanged}
                />
            );
        }

        default:
            return null;
    }
};

export default LessonContent;
